import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import api from '../services/api';
import type { SupaSQBuilding, SupaSQUnit } from '../types/supa';
import { supaBuildingKey } from '../types/supa';

const UNIT_STATUS: Record<string, { label: string; color: 'success' | 'primary' | 'warning' | 'default' }> = {
  '300': { label: 'Available', color: 'success' },
  '301': { label: 'Connected with Sonic', color: 'primary' },
  '302': { label: 'Transfer Available', color: 'warning' },
};

function buildingLabel(b: SupaSQBuilding): string {
  return [
    b.buildingName,
    b.address_street_number && b.address_route ? `${b.address_street_number} ${b.address_route}` : b.address_route,
    b.address_locality,
    b.address_administrative_area_level_1,
    b.address_postal_code,
  ].filter(Boolean).join(', ');
}

function unitLabel(u: SupaSQUnit): string {
  return u.unitNo ? `Unit ${u.unitNo}` : u.addressLine1 || u.rid;
}

function formatUnitAddress(u: SupaSQUnit): string {
  return [
    u.unitNo ? `Unit ${u.unitNo}` : null,
    u.addressLine1,
    u.city,
    u.state,
    u.postcode,
  ].filter(Boolean).join(', ');
}

function formatBuildingAddress(b: SupaSQBuilding): string {
  return [
    b.buildingName,
    b.address_street_number,
    b.address_route,
    b.address_locality,
    b.address_administrative_area_level_1,
    b.address_postal_code,
  ].filter(Boolean).join(', ');
}

export type SupaBuildingSelection = {
  building: SupaSQBuilding;
  buildingKey: string;
  unit: SupaSQUnit | null;
  address: string;
};

type Props = {
  /** Currently confirmed address (shows the "confirmed" banner instead of the search UI). */
  value: string;
  onSelect: (selection: SupaBuildingSelection) => void;
  onClear: () => void;
  initialBuildingKey?: string;
};

type SQState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'units'; units: SupaSQUnit[] }
  | { phase: 'building_only' }
  | { phase: 'not_serviceable'; description: string }
  | { phase: 'error'; message: string };

/** Staff-auth building + unit picker for Supa Networks services — same two-step
 * search UX as the public order flow's SupaSQAddressField, but against the
 * admin API and returning the full selected building/unit (not just a string),
 * so callers can persist building_place_id/building_name/building_key alongside
 * the formatted address. */
export default function SupaBuildingUnitPicker({ value, onSelect, onClear, initialBuildingKey }: Props) {
  const [buildings, setBuildings] = useState<SupaSQBuilding[]>([]);
  const [buildingsLoading, setBuildingsLoading] = useState(true);
  const [buildingsError, setBuildingsError] = useState<string | null>(null);

  const [selectedBuilding, setSelectedBuilding] = useState<SupaSQBuilding | null>(null);
  const [sq, setSq] = useState<SQState>({ phase: 'idle' });
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  // Once the user explicitly asks to change buildings, stop re-applying
  // initialBuildingKey — otherwise the effect below just re-selects the same
  // building the user is trying to get away from.
  const [autoSelectDisabled, setAutoSelectDisabled] = useState(false);

  useEffect(() => {
    api.adminSupaServicedBuildings()
      .then((data) => {
        setBuildings(data.servicedBuildings ?? []);
        setBuildingsLoading(false);
      })
      .catch(() => {
        setBuildingsError('Could not load serviced buildings list');
        setBuildingsLoading(false);
      });
  }, []);

  const handleBuildingSelect = async (b: SupaSQBuilding) => {
    setSelectedBuilding(b);
    setSq({ phase: 'loading' });
    try {
      const data = await api.adminSupaServiceQualification({
        StreetName: b.address_route,
        StreetNo: b.address_street_number || undefined,
        Suburb: b.address_locality,
        State: b.address_administrative_area_level_1 || undefined,
        Postcode: b.address_postal_code || undefined,
      });
      // SQ is queried by street address, and multiple buildings in the same complex
      // can share one — it returns units for the whole complex, so filter to this building.
      const units = (data.buildingUnits ?? []).filter((u) => u.buildingName === b.buildingName);
      if (data.statusCode === '200' && units.length > 0) {
        setSq({ phase: 'units', units });
      } else if (data.statusCode === '200') {
        setSq({ phase: 'building_only' });
        onSelect({ building: b, buildingKey: supaBuildingKey(b), unit: null, address: formatBuildingAddress(b) });
      } else if (data.statusCode === '402' && units.length > 0) {
        setSq({ phase: 'units', units });
      } else {
        setSq({ phase: 'not_serviceable', description: data.description || 'Address is not on the Supa Networks network.' });
      }
    } catch (e: any) {
      setSq({ phase: 'error', message: e?.response?.data?.detail || e?.message || 'Service qualification check failed' });
    }
  };

  useEffect(() => {
    if (autoSelectDisabled || !initialBuildingKey || buildingsLoading || selectedBuilding) return;
    const match = buildings.find((b) => supaBuildingKey(b) === initialBuildingKey);
    if (match) handleBuildingSelect(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBuildingKey, buildingsLoading, buildings, autoSelectDisabled]);

  // Local-only reset — drops back to the search box without telling the
  // parent to clear anything. Used by the inline "Change" shown next to a
  // (possibly auto-selected) building, so the user can search for a
  // different one instead of being stuck on the pre-filled building.
  const handleChangeBuilding = () => {
    setAutoSelectDisabled(true);
    setSelectedBuilding(null);
    setSq({ phase: 'idle' });
    setAutocompleteKey((k) => k + 1);
  };

  // Full reset — also clears the parent's confirmed value. Used by the
  // "Change" action on the already-confirmed banner (value prop set).
  const handleReset = () => {
    handleChangeBuilding();
    onClear();
  };

  const handleSelectUnit = (u: SupaSQUnit) => {
    if (!selectedBuilding) return;
    onSelect({
      building: selectedBuilding,
      buildingKey: supaBuildingKey(selectedBuilding),
      unit: u,
      address: formatUnitAddress(u),
    });
  };

  if (value && sq.phase !== 'units') {
    return (
      <Box>
        <Alert
          severity="success"
          sx={{ py: 0.5 }}
          action={<Button size="small" color="inherit" onClick={handleReset}>Change</Button>}
        >
          <Typography variant="body2" fontWeight={600}>{value}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {!selectedBuilding && (
        buildingsError ? (
          <Alert severity="warning" sx={{ mb: 1 }}>{buildingsError}</Alert>
        ) : (
          <Autocomplete
            key={autocompleteKey}
            options={buildings}
            loading={buildingsLoading}
            filterOptions={(options, state) => {
              const q = state.inputValue.toLowerCase().trim();
              if (!q) return options.slice(0, 100);
              const results = options.filter((b) => {
                const haystack = [
                  b.buildingName,
                  b.address_subpremise,
                  b.address_street_number,
                  b.address_route,
                  `${b.address_street_number ?? ''} ${b.address_route ?? ''}`.trim(),
                  b.address_locality,
                  b.address_postal_code,
                ].filter(Boolean).join(' ').toLowerCase();
                return haystack.includes(q);
              });
              return results.slice(0, 30);
            }}
            getOptionLabel={buildingLabel}
            onChange={(_, b) => { if (b) handleBuildingSelect(b); }}
            noOptionsText={buildingsLoading ? 'Loading…' : 'No matching buildings — try a street name or suburb'}
            renderOption={(props, b) => (
              <li {...props} key={supaBuildingKey(b)}>
                <Box sx={{ py: 0.25 }}>
                  <Typography variant="body2" fontWeight={600}>{b.buildingName.replace(/[\r\n]+/g, ' — ')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[b.address_street_number, b.address_route, b.address_locality, b.address_administrative_area_level_1, b.address_postal_code].filter(Boolean).join(' ')}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Search building"
                placeholder="Type a building name, street or suburb…"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', ml: 0.5 }} />,
                  endAdornment: (
                    <>
                      {buildingsLoading && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )
      )}

      {selectedBuilding && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Typography variant="body2" fontWeight={600}>{selectedBuilding.buildingName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[selectedBuilding.address_street_number, selectedBuilding.address_route, selectedBuilding.address_locality, selectedBuilding.address_administrative_area_level_1, selectedBuilding.address_postal_code].filter(Boolean).join(' ')}
            </Typography>
          </Box>
          <Button size="small" onClick={handleChangeBuilding} sx={{ flexShrink: 0, fontSize: '0.75rem' }}>Change</Button>
        </Box>
      )}

      {sq.phase === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Checking availability…</Typography>
        </Box>
      )}

      {sq.phase === 'units' && (
        <Autocomplete
          options={sq.units}
          getOptionLabel={unitLabel}
          onChange={(_, u) => { if (u) handleSelectUnit(u); }}
          renderOption={(props, u) => {
            const st = UNIT_STATUS[u.statusCode] ?? { label: u.statusCode, color: 'default' as const };
            return (
              <li {...props} key={u.rid}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                  <Typography variant="body2">{unitLabel(u)}</Typography>
                  <Chip label={st.label} color={st.color} size="small" />
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} size="small" label="Select unit / apartment" placeholder="Type or scroll to find the unit…" />
          )}
          noOptionsText="No units found for this building"
        />
      )}

      {sq.phase === 'not_serviceable' && (
        <Alert severity="error" sx={{ mt: 0.5 }} action={<Button size="small" color="inherit" onClick={handleChangeBuilding}>Search again</Button>}>
          {sq.description}
        </Alert>
      )}

      {sq.phase === 'error' && (
        <Alert severity="warning" sx={{ mt: 0.5 }} action={<Button size="small" color="inherit" onClick={handleChangeBuilding}>Try again</Button>}>
          {sq.message}
        </Alert>
      )}
    </Box>
  );
}
